/**
 * Writing a message: recipients, subject, body.
 *
 * Recipients are core's `SubjectMultiPicker` with `useSubjectSearch` — both
 * fetch-free, so they search whatever this dialog hands them, here
 * `/api/messages/recipients` rather than the share dialog's `/api/subjects`.
 *
 * The limits come from `@forsys/shared`, the same numbers the server enforces,
 * so the counter turns red before the server refuses rather than after.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { MESSAGE_LIMITS, type MessageDto, type SubjectDto } from '@forsys/shared';
import { FormDialog } from '../../components/FormDialog';
import { SubjectMultiPicker } from '../../components/SubjectMultiPicker';
import { useSubjectSearch } from '../../hooks/useSubjectSearch';
import { useSubmit } from '../../hooks/useSubmit';
import { searchRecipients, sendMessage } from './api';

export interface ComposeDialogProps {
  open: boolean;
  /** Who it starts addressed to — "Send message" on a user's screen passes them. */
  initialRecipients?: readonly SubjectDto[];
  onClose: () => void;
  onSent?: (message: MessageDto) => void;
}

export function ComposeDialog({ open, initialRecipients, onClose, onSent }: ComposeDialogProps) {
  const { t } = useTranslation('messaging');

  const [recipients, setRecipients] = useState<SubjectDto[]>([]);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const search = useSubjectSearch(searchRecipients, { enabled: open });

  // Fresh every time it opens: a dialog that remembered the last draft would
  // send half of it to the next person. Keyed on `open` alone — a caller's
  // `initialRecipients` is usually a new array each render, and depending on it
  // would wipe the draft on every keystroke.
  useEffect(() => {
    if (!open) return;
    setRecipients([...(initialRecipients ?? [])]);
    setSubject('');
    setBody('');
    search.reset();
  }, [open]);

  const send = useSubmit(() =>
    sendMessage({
      to: recipients.map((recipient) => ({ type: recipient.type, id: recipient.id })),
      subject,
      body,
    }),
  );

  async function submit(): Promise<void> {
    const result = await send.submit();
    if (!result) return;
    onSent?.(result.message);
    onClose();
  }

  const subjectTooLong = subject.trim().length > MESSAGE_LIMITS.subjectLength;
  const bodyTooLong = body.length > MESSAGE_LIMITS.bodyLength;
  const incomplete =
    recipients.length === 0 || subject.trim().length === 0 || body.trim().length === 0;

  return (
    <FormDialog
      open={open}
      title={t('compose.title')}
      submitLabel={t('action.send')}
      pending={send.pending}
      error={send.error}
      disabled={incomplete || subjectTooLong || bodyTooLong}
      maxWidth="md"
      onSubmit={() => void submit()}
      onClose={onClose}
    >
      <Stack spacing={1}>
        <Typography variant="subtitle2">{t('compose.to')}</Typography>
        <SubjectMultiPicker
          value={recipients}
          onChange={setRecipients}
          options={search.options}
          loading={search.loading}
          label={t('compose.toPlaceholder')}
          helperText={t('compose.toHelper')}
          autoFocus={recipients.length === 0}
          onQueryChange={search.setQuery}
        />
      </Stack>

      <TextField
        label={t('compose.subject')}
        value={subject}
        onChange={(event) => setSubject(event.target.value.replace(/[\r\n]/g, ' '))}
        error={subjectTooLong}
        helperText={t('compose.length', { used: subject.trim().length, max: MESSAGE_LIMITS.subjectLength })}
        required
        fullWidth
      />

      <TextField
        label={t('compose.body')}
        value={body}
        onChange={(event) => setBody(event.target.value)}
        error={bodyTooLong}
        helperText={t('compose.length', { used: body.length, max: MESSAGE_LIMITS.bodyLength })}
        required
        fullWidth
        multiline
        minRows={8}
      />
    </FormDialog>
  );
}
